import { useLastEscalation } from '@hakimo-ui/hakimo/data-access';
import { RecentEscalation } from '@hakimo-ui/hakimo/types';
import { Tooltip } from '@hakimo-ui/shared/ui-base';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ExternalLink } from 'lucide-react';
import { getZeroToleranceTextColor } from '../sop-workflow/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export function getEscalationTime(escalation?: RecentEscalation | null) {
  return dayjs
    .utc(escalation?.createdAt)
    .tz(escalation?.location?.timezone || 'UTC')
    .format('YYYY-MM-DD HH:mm:ss');
}

export function getEscalationHref(escalation?: RecentEscalation | null) {
  if (!escalation?.id || !escalation?.source_system) {
    return undefined;
  }
  return escalation.source_system === 'Vision'
    ? `/alarm-groups/${escalation.id}`
    : `/location-alarms/${escalation.id}`;
}

export interface LastEscalationData {
  description?: string;
  hasError: boolean;
  hasEscalation: boolean;
  href?: string;
  label: string;
}

export function useLastEscalationInfo(locationId: string): LastEscalationData {
  const { data, isLoading, error } = useLastEscalation(locationId);

  const escalation = data?.escalation;
  const hasEscalation = !isLoading && !error && !!escalation?.createdAt;

  return {
    description: escalation?.description,
    hasError: !!error,
    hasEscalation,
    href: getEscalationHref(escalation),
    label: isLoading
      ? 'Loading...'
      : error
      ? 'Error Loading Escalation'
      : hasEscalation
      ? getEscalationTime(escalation)
      : 'No escalation data',
  };
}

interface ValueProps {
  info: LastEscalationData;
  showTooltip?: boolean;
  isZeroToleranceSite?: boolean;
}

export function LastEscalationValue(props: ValueProps) {
  const { info, showTooltip = true, isZeroToleranceSite } = props;

  const infoIcon = showTooltip && (
    <InformationCircleIcon className="h-4 w-4 stroke-2" />
  );

  return (
    <div
      className={clsx(
        'flex items-center gap-2 text-xs font-semibold',
        info.hasError
          ? 'text-red-500'
          : getZeroToleranceTextColor(isZeroToleranceSite)
      )}
    >
      <span>{info.label}</span>
      {info.hasEscalation && infoIcon ? (
        <Tooltip
          text={info.description || 'No description'}
          colorModifier="info"
          size="medium"
          position="bottom"
        >
          {infoIcon}
        </Tooltip>
      ) : (
        infoIcon
      )}
    </div>
  );
}

interface Props {
  locationId: string;
  showTooltip?: boolean;
  isZeroToleranceSite?: boolean;
  layout?: 'value' | 'stacked';
}

export function LastEscalationInfo(props: Props) {
  const {
    locationId,
    showTooltip = true,
    isZeroToleranceSite,
    layout = 'value',
  } = props;

  const info = useLastEscalationInfo(locationId);

  const value = (
    <LastEscalationValue
      info={info}
      showTooltip={showTooltip}
      isZeroToleranceSite={isZeroToleranceSite}
    />
  );

  if (layout === 'value') {
    return value;
  }

  return (
    <div className="flex flex-col">
      <div className="text-onlight-text-1 dark:text-ondark-text-1 flex items-center gap-2 font-sans font-semibold">
        <span>Last Escalated</span>
        {info.href && (
          <a
            href={info.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
      {value}
    </div>
  );
}
